import dayjs from 'dayjs';
import protobuf from 'protobufjs';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { metricsAxios, proxyAxios } from 'api/apiBase/axios';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { cexExchangesTimeframes, IChartTimeframe, KLine } from 'types/charts';
import { chartTimeframeToInterval } from 'utils/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';
import { aggregateOrderbook, mergeOrderBook, sortOrderbookFunc } from '../utils';

// REST calls go through the external proxy, where a request can arrive late or
// not at all. The UI is already bounded by bootstrapStrategy, so seed requests
// get a long timeout and are retried instead of being dropped after 5 s.
export const MEXC_SEED_TIMEOUT_MS = 20000;
export const MEXC_SEED_ATTEMPTS = 3;
const MEXC_SEED_RETRY_DELAY_MS = 1000;
const MEXC_MAX_PENDING_DIFFS = 2000;

type DepthDiff = { toVersion: number; asks: string[][]; bids: string[][] };

export class MexcStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pingInterval: ReturnType<typeof setInterval> | undefined;

  bids: string[][];
  asks: string[][];
  pair: string;
  metricsSymbol: string;
  displayDepth: number; // User requested depth (20 or 50)
  fetchDepth = 1000; // Always fetch more data
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  selectedPrecision: number;
  // The depth stream only carries changed levels. Until the REST snapshot has
  // landed, diffs are buffered instead of painting a book of a few stray levels.
  orderbookSeeded = false;
  pendingDiffs: DepthDiff[] = [];
  messageProto: protobuf.Type | undefined;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    fetch('/mexc-protos/protos.proto').then(async res => {
      const protoText = await res.text();

      const root = protobuf.parse(protoText).root;

      this.messageProto = root.lookupType('PushDataV3ApiWrapper');
    });

    this.pair = `${base.toUpperCase()}${quote.toUpperCase()}`;
    this.metricsSymbol = `${base.toUpperCase()}-${quote.toUpperCase()}`;
    this.bids = [];
    this.asks = [];
    this.displayDepth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  setOrderbookDepth(depth: number) {
    this.displayDepth = depth;
  }

  fetchTickerPrecision = async () => {
    const applyPrecision = (prec: number) => {
      this.precision = prec;
      this.selectedPrecision = prec;
      this.precisionUpdate(prec);
    };

    // The metrics service already stores the venue's symbol precision. Use it
    // first so chart/orderbook startup does not depend on the external proxy.
    try {
      const res = await metricsAxios.get<{ tick_size: string }>('/api/v1/market/precision', {
        params: { symbol: this.metricsSymbol, cex: ECexName.mexc },
        timeout: 5000,
      });
      const tick = Number(res.data?.tick_size);
      const normalized = Number.isFinite(tick) && tick > 0 ? normalizeNumber(tick) : '';
      if (Number(normalized) > 0) {
        applyPrecision(normalized.split('.')[1]?.length ?? 0);
        return;
      }
    } catch {
      // Keep a bounded public-venue fallback for absent/stale cache entries.
    }

    try {
      const url = new URL('https://api.mexc.com/api/v3/exchangeInfo');
      url.search = new URLSearchParams({ symbol: this.pair }).toString();

      const res = await proxyAxios.get<{
        symbols?: { symbol: string; quoteAssetPrecision?: number; quotePrecision?: number }[];
      }>('/api/v1/proxy', {
        params: { url: url.toString() },
        timeout: 5000,
      });

      const symbol = res.data?.symbols?.find(item => item.symbol === this.pair);
      const prec = symbol?.quoteAssetPrecision ?? symbol?.quotePrecision;
      if (!Number.isInteger(prec) || prec! < 0) {
        throw new Error('Mexc price precision unavailable');
      }
      applyPrecision(prec!);
    } catch (error) {
      console.log('Mexc ticker error: ', error);
    }
  };

  publishOrderbook = () => {
    const orderbook = aggregateOrderbook(
      this.asks,
      this.bids,
      this.selectedPrecision,
      this.displayDepth,
    );

    this.onOrderbookUpdate(orderbook);

    return orderbook;
  };

  applyDepthDiff = ({ asks, bids }: DepthDiff) => {
    this.asks = mergeOrderBook(this.asks, asks).sort(sortOrderbookFunc).slice(0, this.fetchDepth);
    this.bids = mergeOrderBook(this.bids, bids).sort(sortOrderbookFunc).slice(-this.fetchDepth);
  };

  applySnapshot = ({
    asks,
    bids,
    lastUpdateId,
  }: {
    asks: string[][];
    bids: string[][];
    lastUpdateId?: number | string;
  }) => {
    this.asks = asks;
    this.bids = bids;

    // Replay diffs that arrived while the snapshot was in flight.
    const snapshotVersion = Number(lastUpdateId);
    this.pendingDiffs
      .filter(diff => !Number.isFinite(snapshotVersion) || diff.toVersion > snapshotVersion)
      .forEach(this.applyDepthDiff);
    this.pendingDiffs = [];
    this.orderbookSeeded = true;

    return this.publishOrderbook();
  };

  fetchOrderbook = async () => {
    const url = new URL('https://api.mexc.com/api/v3/depth');
    url.search = new URLSearchParams({
      symbol: this.pair,
      limit: this.fetchDepth.toString(),
    }).toString();

    for (let attempt = 1; attempt <= MEXC_SEED_ATTEMPTS; attempt += 1) {
      try {
        const res = await proxyAxios.get<{
          lastUpdateId?: number;
          asks: [string, string][];
          bids: [string, string][];
        }>('/api/v1/proxy', {
          params: { url: url.toString() },
          timeout: MEXC_SEED_TIMEOUT_MS,
        });

        if (Array.isArray(res.data?.asks) && Array.isArray(res.data?.bids)) {
          return this.applySnapshot(res.data);
        }
        console.log('Mexc orderbook error: unexpected snapshot', res.data);
      } catch (error) {
        console.log('Mexc orderbook error: ', error);
      }

      if (attempt < MEXC_SEED_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, MEXC_SEED_RETRY_DELAY_MS));
      }
    }
  };

  fetchKlines = async ({
    base,
    quote,
    limit,
    interval,
    from,
    to,
  }: {
    base: string;
    quote: string;
    limit: number;
    interval: IChartTimeframe;
    from?: number;
    to?: number;
  }) => {
    try {
      const innerInterval =
        cexExchangesTimeframes[interval]?.[ECexName.mexc] ??
        cexExchangesTimeframes['1m'][ECexName.mexc];
      const intervalMs = chartTimeframeToInterval(interval);
      const cappedLimit = Math.min(Math.max(limit, 1), 1000);

      const url = new URL('https://api.mexc.com/api/v3/klines');
      const params = new URLSearchParams();
      params.append('symbol', `${base}${quote}`.toUpperCase());
      params.append('interval', String(innerInterval));
      params.append('limit', cappedLimit.toString());

      const endTime = Number.isFinite(to) ? Number(to) : undefined;
      const startTime = Number.isFinite(from)
        ? Number(from)
        : endTime && intervalMs
          ? endTime - Math.max(1, cappedLimit - 1) * intervalMs
          : undefined;

      if (startTime !== undefined) {
        params.append('startTime', Math.max(0, Math.floor(startTime)).toString());
      }
      if (endTime !== undefined) {
        params.append('endTime', Math.max(0, Math.floor(endTime)).toString());
      }

      url.search = params.toString();

      const result = await proxyAxios.get<(number | string)[][]>('/api/v1/proxy', {
        params: {
          url: url.toString(),
        },
      });

      return (result.data ?? []).map(el => [
        Number(el[0]),
        el[1],
        el[2],
        el[3],
        el[4],
        el[5],
      ]) as KLine[];
    } catch (error) {
      console.log('Mexc klines error: ', error);

      return [];
    }
  };

  fetchTrades = async ({ base, quote, limit }: { base: string; quote: string; limit: number }) => {
    try {
      const url = new URL('https://api.mexc.com/api/v3/trades');
      const params = new URLSearchParams({
        symbol: `${base}${quote}`.toUpperCase(),
        limit: limit.toString(),
      });

      url.search = params.toString();

      const result = await proxyAxios.get<
        {
          isBuyerMaker: boolean;
          price: string;
          qty: string;
          time: string;
        }[]
      >('/api/v1/proxy', { params: { url: url.toString() }, timeout: MEXC_SEED_TIMEOUT_MS });

      return result.data?.map(item => ({
        price: item.price,
        quantity: item.qty,
        timestamp: item.time,
        side: item.isBuyerMaker ? 'sell' : 'buy',
      }));
    } catch (error) {
      console.log('Mexc trades error: ', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    if (this.ws) {
      this.closeWebSocket();
    }

    const ws = new ReconnectingWebSocket('wss://wbs-api.mexc.com/ws', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;

    const intervalsMap = (_interval: number) =>
      ({
        60000: 'Min1',
        300000: 'Min5',
        900000: 'Min15',
        3600000: 'Min60',
        14400000: 'Hour4',
        86400000: 'Day1',
        604800000: 'Week1',
      })[_interval] ?? _interval;

    const userTimezone = dayjs.tz.guess();
    const offsetHours = dayjs().tz(userTimezone).utcOffset() / 60;
    const formattedTimezone = `UTC${offsetHours >= 0 ? '+' : ''}${offsetHours}`;

    const tickerEvent = `spot@public.miniTicker.v3.api.pb@${this.pair}@${formattedTimezone}`;
    const klineEvent = `spot@public.kline.v3.api.pb@${this.pair}@${intervalsMap(interval)}`;
    const depthEvent = `spot@public.aggre.depth.v3.api.pb@100ms@${this.pair}`;
    const tradeEvent = `spot@public.aggre.deals.v3.api.pb@100ms@${this.pair}`;

    this.pingInterval = setInterval(() => {
      if (this.ws) {
        this.ws.send(JSON.stringify({ method: 'PING' }));
      }
    }, 60 * 1000);

    this.ws.onopen = () => {
      ws.send(
        JSON.stringify({
          method: 'SUBSCRIPTION',
          params: [depthEvent, klineEvent, tradeEvent, tickerEvent],
        }),
      );
    };

    this.ws.onmessage = async event => {
      try {
        if (!this.messageProto) return;

        if (event.data instanceof Blob) {
          const arrayBuffer = await event.data.arrayBuffer();
          const buffer = new Uint8Array(arrayBuffer);

          const messBuff = this.messageProto.decode(buffer) ?? undefined;
          const message = messBuff
            ? this.messageProto.toObject(messBuff, {
                longs: String,
                enums: String,
                bytes: String,
              })
            : undefined;

          if (!message) return;

          if (message.channel === klineEvent) {
            const { volume, closingPrice, highestPrice, lowestPrice, openingPrice, windowStart } =
              message.publicSpotKline;

            this.klineUpdate([
              Number(windowStart),
              openingPrice,
              highestPrice,
              lowestPrice,
              closingPrice,
              volume,
            ]);
          }

          if (message.channel === depthEvent) {
            const { asks: a, bids: b, toVersion } = message.publicAggreDepths;

            const diff: DepthDiff = {
              toVersion: Number(toVersion),
              asks: ((a ?? []) as any[]).map(el => [el.price, el.quantity]) as string[][],
              bids: ((b ?? []) as any[]).map(el => [el.price, el.quantity]) as string[][],
            };

            if (!this.orderbookSeeded) {
              this.pendingDiffs.push(diff);
              if (this.pendingDiffs.length > MEXC_MAX_PENDING_DIFFS) this.pendingDiffs.shift();
              return;
            }

            this.applyDepthDiff(diff);
            this.publishOrderbook();
          }

          if (message.channel === tradeEvent) {
            const { price, quantity, time, tradeType } = message.publicAggreDeals?.dealsList?.[0];

            this.onTradeUpdate({
              price,
              quantity: quantity,
              timestamp: time,
              side: tradeType === 1 ? 'buy' : 'sell',
            });
          }

          if (message.channel === tickerEvent) {
            const { volume, rate, price, high, low, quantity } = message.publicMiniTicker;

            this.onTickerUpdate({
              baseVolume: quantity,
              change: normalizeNumber(Number(rate) * 100),
              close: price,
              high: high,
              last: Number(price).toFixed(this.precision),
              low: low,
              quoteVolume: volume,
            });
          }
        }
      } catch (error) {
        console.log('Mexc socket error', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;

    this.onOrderbookUpdate(
      aggregateOrderbook(this.asks, this.bids, this.selectedPrecision, this.displayDepth),
    );
  };

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
    await this.fetchOrderbook();
  };

  static init = async (args: IInitArgs) => {
    const instance = new MexcStrategy(args);

    await bootstrapStrategy(instance);

    return instance;
  };
}
